<template>
  <v-dialog :value="true" width="auto" scrollable persistent>
    <bm-card
      :label="
        $m.error_has_occurred +
        (errorCode ? ` - ${errorCode}` : '') +
        ` - ${timestamp()}`
      "
    >
      <v-card-text data-qa="error-text"> {{ errorText }}</v-card-text>
      <template v-if="jsStack.length || serverStack.length || endpointName">
        <v-divider />
        <template v-if="expanded">
          <v-card-text v-if="endpointName"> {{ endpointName }}</v-card-text>
          <v-card-text class="stacktrace">
            <div v-for="(line, index) of jsStack" :key="index">
              {{ line }}
            </div>
          </v-card-text>
          <v-card-text class="stacktrace"
            ><div v-for="(line, index) of serverStack" :key="index">
              {{ line }}
            </div></v-card-text
          >
        </template>

        <bm-btn-expand :value.sync="expanded" block></bm-btn-expand>
      </template>

      <template #controls>
        <v-spacer />
        <bm-btn @click="accept">{{ $m.close }}</bm-btn>
      </template>
    </bm-card>
  </v-dialog>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmBtnExpand from "@/components/controls/input/BmBtnExpand.vue";
import BmCard from "@/components/controls/card/BmCard.vue";
import { BrewManApiError, errorStatusCodeToTextLookup } from "@/app/errors";
import { makeRef } from "@/components/composables/componentTools";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import userStore from "@/store/modules/userStore";
import { isRunningEndToEndTests } from "@/app/ui/utilities";
import { computed } from "vue";

interface BmErrorDialogProps {
  error: string | Error;
}

interface BmErrorDialogEvents extends DialogEvents {}

const props = defineProps<BmErrorDialogProps>();
const emit = defineEmits<BmErrorDialogEvents>();

const { accept } = useReactiveDialogScriptSetup(emit);

const expanded = makeRef(
  !!userStore.state.user?.userTenantGroupLevel.isSuperAdministrator() ||
    isRunningEndToEndTests()
);

const errorText = computed(() => {
  const err = props.error;
  if (typeof err === "string") {
    return err;
  }

  if (err instanceof Error) {
    return err.message;
  }

  return "";
});

const jsStack = computed(() => {
  const err = props.error;
  if (err instanceof Error) {
    return (err.stack && err.stack.split(/(\r\n|\n|\r)/)) || [];
  }

  return [];
});

const serverStack = computed(() => {
  const err = props.error;
  if (err instanceof BrewManApiError) {
    return (
      (err.serverException && err.serverException.split(/(\r\n|\n|\r)/)) || []
    );
  }

  return [];
});

const errorCode = computed(() => {
  const err = props.error;
  if (err instanceof BrewManApiError) {
    return errorStatusCodeToTextLookup[err.code] || "";
  }

  return "";
});

const endpointName = computed(() => {
  const err = props.error;
  if (err instanceof BrewManApiError) {
    return err.endpoint || "";
  } else {
    return "";
  }
});

/**
 * Not a computed getter so to not cache the timestamp
 */
function timestamp(): string {
  const date = new Date();
  let timezoneFormatted = (-1 * date.getTimezoneOffset()) / 60;
  const tzString =
    (timezoneFormatted < 0 ? "-" : "+") + timezoneFormatted.toFixed(1);
  return `${date.toLocaleString()} (${tzString})`;
}
</script>

<style scoped lang="sass">
/* Make stacktrace monospaced and indented */
.stacktrace
  font-family: "Courier New", Courier, monospace

.stacktrace div
  margin-left: 2em

.stacktrace div:first-child
  margin-left: 0
</style>
